import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase, handleAuthRefreshError, safeGetSession } from '@/lib/supabase';
import { fetchPortalProfile } from '@/lib/portalProfile';

interface AuthGuardProps {
  children: React.ReactNode;
  requiredRole: 'management' | 'subcontractor' | 'client';
}

const loginRedirect: Record<string, string> = {
  management: '/login/management',
  subcontractor: '/login/subcontractor',
  client: '/login/client',
};

type LoadState = 'loading' | 'authorized' | 'unauthorized';

export default function AuthGuard({ children, requiredRole }: AuthGuardProps) {
  const [state, setState] = useState<LoadState>('loading');
  const navigate = useNavigate();

  useEffect(() => {
    let cancelled = false;

    async function check() {
      const startedAt = performance.now();
      const logTiming = (phase: string) => {
        console.log(`[AuthGuard] ⏱ ${phase}: ${(performance.now() - startedAt).toFixed(0)}ms`);
      };
      console.log('[AuthGuard] Checking access for requiredRole:', requiredRole);

      try {
        let sessionResult = await safeGetSession();
        logTiming('getSession');

        let session = sessionResult.data.session;
        if (!session && sessionResult.timedOut) {
          console.warn('[AuthGuard] getSession timed out — retrying once');
          await new Promise((resolve) => setTimeout(resolve, 400));
          const retry = await supabase.auth.getSession();
          session = retry.data.session;
          sessionResult = { data: { session }, error: retry.error };
        }

        const sessionError = sessionResult.error;

        if (sessionError) {
          const cleared = await handleAuthRefreshError(sessionError);
          if (cleared) {
            console.log('[AuthGuard] Invalid refresh token detected — redirecting to login');
            if (!cancelled) {
              setState('unauthorized');
              navigate(loginRedirect[requiredRole], { replace: true });
            }
            return;
          }
        }

        if (!session?.user) {
          console.log('[AuthGuard] No session — redirecting to login');
          if (!cancelled) {
            setState('unauthorized');
            navigate(loginRedirect[requiredRole], { replace: true });
          }
          return;
        }

        const { profile, source } = await fetchPortalProfile(session.user.id, session.user.email);

        logTiming('profile fetch');
        console.log('[AuthGuard] Portal profile result:', profile, 'source:', source);

        if (cancelled) return;

        if (!profile) {
          console.log('[AuthGuard] Profile not found for user_id:', session.user.id);
          if (!cancelled) {
            setState('unauthorized');
            navigate(loginRedirect[requiredRole], { replace: true });
          }
          return;
        }

        if (!profile.is_active) {
          console.log('[AuthGuard] Profile is inactive for user_id:', session.user.id);
          if (!cancelled) {
            setState('unauthorized');
            navigate(loginRedirect[requiredRole], { replace: true });
          }
          return;
        }

        const userType = (profile.user_type ?? '').toLowerCase();
        console.log('[AuthGuard] userType from profile:', userType, 'requiredRole:', requiredRole);

        if (userType === requiredRole) {
          console.log('[AuthGuard] Access granted.');
          logTiming('authorized');
          if (!cancelled) setState('authorized');
        } else {
          console.log('[AuthGuard] Role mismatch — redirecting to correct portal');
          if (!cancelled) {
            setState('unauthorized');
            if (userType === 'management') {
              navigate('/management/dashboard', { replace: true });
            } else if (userType === 'subcontractor') {
              navigate('/subcontractor/dashboard', { replace: true });
            } else if (userType === 'client') {
              navigate('/client/portal', { replace: true });
            } else {
              await supabase.auth.signOut({ scope: 'local' });
              navigate(loginRedirect[requiredRole], { replace: true });
            }
          }
        }
      } catch (err) {
        console.log('[AuthGuard] Unexpected error during auth check:', err);
        const cleared = await handleAuthRefreshError(err);
        if (cleared) {
          console.log('[AuthGuard] Invalid refresh token from thrown error — redirecting to login');
        }
        if (!cancelled) {
          setState('unauthorized');
          navigate(loginRedirect[requiredRole], { replace: true });
        }
      }
    }

    check();

    // Only react to explicit SIGNED_OUT to avoid false logouts
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event) => {
      if (event === 'SIGNED_OUT' && !cancelled) {
        console.log('[AuthGuard] SIGNED_OUT event received');
        setState('unauthorized');
        navigate(loginRedirect[requiredRole], { replace: true });
      }
    });

    return () => {
      cancelled = true;
      subscription.unsubscribe();
    };
  }, [requiredRole, navigate]);

  if (state === 'loading') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F9FAFB]">
        <div className="flex flex-col items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-[#E8621A] flex items-center justify-center">
            <span className="text-white font-bold text-sm">JH</span>
          </div>
          <div className="flex items-center gap-2 text-[#9CA3AF] text-sm">
            <i className="ri-loader-4-line animate-spin text-lg text-[#E8621A]"></i>
            Loading...
          </div>
        </div>
      </div>
    );
  }

  if (state !== 'authorized') return null;

  return <>{children}</>;
}